import { instantToFormTime, formTimeToInstant } from "./form-time";
import { type ShiftFormValidationIssue, validationMessage } from "./form-state";

type Values = {
  startedAt: string | null;
  delayReason: string | null;
  firstProductAt: string | null;
  firstProductDelayReason: string | null;
};

export function ShiftFormTimeFields({
  values,
  productionDate = null,
  onChange,
  label,
  separate = false,
  validationIssues,
  validationPrefix,
  required = false,
}: {
  values: Values;
  productionDate?: string | null;
  onChange: (values: Values) => void;
  label: string;
  separate?: boolean;
  validationIssues: readonly ShiftFormValidationIssue[];
  validationPrefix: string;
  required?: boolean;
}) {
  const startField = separate ? "postChangeMachineStart" : "machineStart";
  const firstProductField = separate ? "postChangeFirstProduct" : "firstProduct";
  const startError = validationMessage(validationIssues, `${validationPrefix}.${startField}`);
  const firstProductError = validationMessage(validationIssues, `${validationPrefix}.${firstProductField}`);
  return (
    <section
      className={`shift-paper-scroll${separate ? " shift-post-change-times" : ""}`}
      aria-label={label}
    >
      <table
        className="shift-paper-table shift-pdf-time-table"
        aria-label={label}
      >
        <colgroup>
          <col className="shift-time-column" />
          <col />
        </colgroup>
        <tbody>
          <tr>
            <th scope="col">Makina Start Zamanı:{required ? <RequiredMark /> : null}</th>
            <th scope="col">Geç Start Verildiyse Sebebi:</th>
          </tr>
          <tr>
            <td>
              <input
                data-shift-validation={`${validationPrefix}.${startField}`}
                data-shift-diff-path={`${validationPrefix}.${separate ? "postChangeMachineStartedAt" : "machineStartedAt"}`}
                aria-invalid={Boolean(startError)}
                aria-describedby={startError ? `shift-error-${validationPrefix}-${startField}` : undefined}
                aria-label={`${label} / Makina Start Zamanı`}
                type="time"
                disabled={!productionDate}
                value={instantToFormTime(values.startedAt)}
                onChange={(event) =>
                  onChange({
                    ...values,
                    startedAt: formTimeToInstant(
                      event.target.value,
                      productionDate,
                    ),
                  })
                }
              />
              <InlineError id={`shift-error-${validationPrefix}-${startField}`} message={startError} />
            </td>
            <td>
              <input
                aria-label={`${label} / Geç Start Verildiyse Sebebi`}
                data-shift-diff-path={`${validationPrefix}.${separate ? "postChangeDelayReason" : "delayReason"}`}
                placeholder="Varsa nedenini yazın"
                value={values.delayReason ?? ""}
                onChange={(event) =>
                  onChange({
                    ...values,
                    delayReason: event.target.value || null,
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <th scope="col">Makinadan Ürün Çıkma Zamanı:{required ? <RequiredMark /> : null}</th>
            <th scope="col">Ürün Geç Çıkma Sebebi:</th>
          </tr>
          <tr>
            <td>
              <input
                data-shift-validation={`${validationPrefix}.${firstProductField}`}
                data-shift-diff-path={`${validationPrefix}.${separate ? "postChangeFirstProductAt" : "firstProductAt"}`}
                aria-invalid={Boolean(firstProductError)}
                aria-describedby={firstProductError ? `shift-error-${validationPrefix}-${firstProductField}` : undefined}
                aria-label={`${label} / Makinadan Ürün Çıkma Zamanı`}
                type="time"
                disabled={!productionDate}
                value={instantToFormTime(values.firstProductAt)}
                onChange={(event) =>
                  onChange({
                    ...values,
                    firstProductAt: formTimeToInstant(
                      event.target.value,
                      productionDate,
                      values.startedAt,
                    ),
                  })
                }
              />
              <InlineError id={`shift-error-${validationPrefix}-${firstProductField}`} message={firstProductError} />
            </td>
            <td>
              <input
                aria-label={`${label} / Ürün Geç Çıkma Sebebi`}
                data-shift-diff-path={`${validationPrefix}.${separate ? "postChangeFirstProductDelayReason" : "firstProductNotOccurredReason"}`}
                placeholder="Varsa nedenini yazın"
                value={values.firstProductDelayReason ?? ""}
                onChange={(event) =>
                  onChange({
                    ...values,
                    firstProductDelayReason: event.target.value || null,
                  })
                }
              />
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

function RequiredMark() {
  return <span className="shift-required-mark" aria-label="zorunlu">*</span>;
}
function InlineError({ id, message }: { id: string; message: string | null }) {
  return message ? <small id={id} className="shift-field-error" role="alert">{message}</small> : null;
}
